import { getPerUnit } from '../../../../api/binder'
import { IBinderModel } from '../../../../interfaces/models/binder.interfaces'
import { sortStringsAlphabetically } from '../../../../helpers/sorting'
import { downloadCsv } from '../../../../helpers/csv'
import { BinderType } from './helper'

const FETCH_BATCH_SIZE = 8

export type BinderCsvLabels = {
  adress: string
  aptNumber: string
  folder: string
  folderDescription: string
  document: string
  published: string
  yes: string
  no: string
}

type BinderCsvRow = {
  adress: string
  aptNumber: string
  folder: string
  folderDescription: string
  document: string
  published: string
}

const getFolderPath = (
  folderId: string | null | undefined,
  foldersById: Map<string, IBinderModel>
): string => {
  const names: string[] = []
  let current = folderId ? foldersById.get(folderId) : undefined
  while (current) {
    names.unshift(current.name || current.filename)
    current = current.parentId ? foldersById.get(current.parentId) : undefined
  }
  return names.join('/')
}

export const buildUnitRows = (
  unit: BinderType,
  items: IBinderModel[],
  publishedLabel: string
): BinderCsvRow[] => {
  const baseRow = {
    adress: unit.adress,
    aptNumber: unit.aptNumber,
    published: publishedLabel,
  }
  if (items.length === 0) {
    return [{ ...baseRow, folder: '', folderDescription: '', document: '' }]
  }

  const folders = items.filter((item) => item.type === 'folder')
  const foldersById = new Map(folders.map((folder) => [folder._id, folder]))
  const documents = items.filter((item) => item.type !== 'folder')

  const rows = documents.map((document) => {
    const parent = document.parentId
      ? foldersById.get(document.parentId)
      : undefined
    return {
      ...baseRow,
      folder: getFolderPath(document.parentId, foldersById),
      folderDescription: parent?.description ?? '',
      document: document.name || document.filename,
    }
  })

  // Folders without documents directly inside still get a row of their own,
  // so every folder (and its description) appears in the export
  const folderIdsWithDocuments = new Set(
    documents.flatMap((document) =>
      document.parentId ? [document.parentId] : []
    )
  )
  folders
    .filter((folder) => !folderIdsWithDocuments.has(folder._id))
    .forEach((folder) => {
      rows.push({
        ...baseRow,
        folder: getFolderPath(folder._id, foldersById),
        folderDescription: folder.description ?? '',
        document: '',
      })
    })

  return rows.sort((a, b) => {
    const folderSort = sortStringsAlphabetically(a.folder, b.folder)
    if (folderSort !== 0) return folderSort
    return sortStringsAlphabetically(a.document, b.document)
  })
}

type FetchTask = { unitId: string; folderId?: string }

// getPerUnit only returns one folder level at a time (plus the folder's
// ancestors), so walk every unit's folder tree breadth-first, batching
// requests across units and folders
const fetchAllUnitItems = async (
  societyId: string,
  units: BinderType[]
): Promise<Map<string, IBinderModel[]>> => {
  const itemsByUnitId = new Map<string, Map<string, IBinderModel>>(
    units.map((unit) => [unit.unitId, new Map()])
  )
  const queuedFolderKeys = new Set<string>()
  const queue: FetchTask[] = units
    .filter((unit) => (unit.fileCount ?? 0) > 0 || (unit.folderCount ?? 0) > 0)
    .map((unit) => ({ unitId: unit.unitId }))

  while (queue.length > 0) {
    const batch = queue.splice(0, FETCH_BATCH_SIZE)
    // eslint-disable-next-line no-await-in-loop
    const responses = await Promise.all(
      batch.map((task) => getPerUnit(societyId, task.unitId, task.folderId))
    )
    responses.forEach((response, index) => {
      const { unitId } = batch[index]
      const unitItems = itemsByUnitId.get(unitId)
      response.data.data.binderData.forEach((item) => {
        unitItems?.set(item._id, item)
        const folderKey = `${unitId}:${item._id}`
        if (item.type === 'folder' && !queuedFolderKeys.has(folderKey)) {
          queuedFolderKeys.add(folderKey)
          queue.push({ unitId, folderId: item._id })
        }
      })
    })
  }

  return new Map(
    Array.from(itemsByUnitId, ([unitId, items]) => [
      unitId,
      Array.from(items.values()),
    ])
  )
}

export const exportBindersToCsv = async (
  societyId: string,
  units: BinderType[],
  labels: BinderCsvLabels
): Promise<void> => {
  const contentsByUnitId = await fetchAllUnitItems(societyId, units)

  const rows = units.flatMap((unit) =>
    buildUnitRows(
      unit,
      contentsByUnitId.get(unit.unitId) ?? [],
      unit.publishedAt ? labels.yes : labels.no
    )
  )

  const header = [
    labels.adress,
    labels.aptNumber,
    labels.folder,
    labels.folderDescription,
    labels.document,
    labels.published,
  ]
  const data = rows.map((row) => [
    row.adress,
    row.aptNumber,
    row.folder,
    row.folderDescription,
    row.document,
    row.published,
  ])

  const date = new Date().toISOString().slice(0, 10)
  downloadCsv(data, `boparmar-${date}.csv`, header)
}
